import React, { useState } from 'react';
import { Shield, ShieldAlert, Plus, UserCheck, AlertTriangle, Trash2, Mail, BadgeCheck } from 'lucide-react';
import { CRMUser, UserRole } from '../lib/db';

const ROLE_META: Record<UserRole, { label: string; desc: string; badge: string; iconBg: string }> = {
  admin: { label: 'Administrator', desc: 'Full access to everything, including User Access Control', badge: 'text-red-600 bg-red-50 dark:bg-red-950/50', iconBg: 'bg-red-50 dark:bg-red-950/40 text-red-600' },
  editor: { label: 'CRM Editor', desc: 'Full access to CRM & System Maintenance (except User Access Control)', badge: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-950/50', iconBg: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600' },
  sales: { label: 'Sales Agent', desc: 'Standard CRM access', badge: 'text-blue-600 bg-blue-50 dark:bg-blue-950/50', iconBg: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600' },
  warehouse: { label: 'Warehouse Agent', desc: 'Directory, Analytics, and Non-Floral LTL only', badge: 'text-amber-600 bg-amber-50 dark:bg-amber-950/50', iconBg: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600' },
};

interface UserMgmtProps {
  users: CRMUser[];
  onAddUser: (email: string, name: string, role: UserRole) => Promise<string | void>;
  onUpdateRole: (uid: string, role: UserRole) => Promise<void>;
  onDeleteUser?: (uid: string) => Promise<void>;
  onImpersonateUser?: (user: CRMUser) => void;
  currentUserEmail?: string | null;
}

export default function UserMgmt({
  users,
  onAddUser,
  onUpdateRole,
  onDeleteUser,
  onImpersonateUser,
  currentUserEmail,
}: UserMgmtProps) {
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('sales');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ success: boolean; msg: string } | null>(null);

  // Strictly enforce 1 entry per user by email, picking highest privilege role
  const ROLE_PRIORITY: Record<UserRole, number> = {
    admin: 4,
    editor: 3,
    sales: 2,
    warehouse: 1,
  };

  const dedupedUsers = React.useMemo(() => {
    const map = new Map<string, CRMUser>();
    for (const u of users) {
      const email = (u.email || '').trim().toLowerCase();
      if (!email) continue;
      const existing = map.get(email);
      if (!existing) {
        map.set(email, u);
      } else {
        const pNew = ROLE_PRIORITY[u.role] || 0;
        const pOld = ROLE_PRIORITY[existing.role] || 0;
        if (pNew > pOld) {
          map.set(email, u);
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => 
      (a.displayName || a.email).localeCompare(b.displayName || b.email)
    );
  }, [users]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !newName) {
      setStatusMsg({ success: false, msg: 'Please fill in both Email and Name.' });
      return;
    }
    
    setLoading(true);
    setStatusMsg(null);
    try {
      const note = await onAddUser(newEmail, newName, newRole);
      setStatusMsg({ success: true, msg: note || `Successfully added ${newName} to ARMELLINI CRM as a ${newRole}!` });
      setNewEmail('');
      setNewName('');
      setNewRole('sales');
    } catch (err: any) {
      setStatusMsg({ success: false, msg: err.message || 'Failed to add user.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (user: CRMUser, nextRole: UserRole) => {
    if (nextRole === user.role) return;
    const confirmed = window.confirm(`Change ${user.displayName}'s role to ${ROLE_META[nextRole].label}? (${ROLE_META[nextRole].desc})`);
    if (!confirmed) return;

    try {
      await onUpdateRole(user.uid, nextRole);
    } catch (err) {
      alert('Failed to update role');
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-white dark:bg-slate-900 transition-colors">
      <div className="mb-6 border-b border-slate-100 dark:border-slate-800 pb-5">
        <h1 className="text-2xl font-extrabold tracking-tight text-black dark:text-white flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-red-600" /> Admin User Access Control
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Add new team members, manage enterprise roles, and audit access credentials for security.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        
        {/* Form panel to add user */}
        <div className="lg:col-span-1 bg-slate-50 dark:bg-slate-950 p-5 rounded-xl border border-slate-200/80 dark:border-slate-800/80">
          <h3 className="text-sm font-extrabold text-slate-850 dark:text-slate-100 uppercase tracking-wider mb-4 flex items-center gap-1.5">
            <Plus className="w-4 h-4 text-blue-600" /> Provision New Team Member
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Corporate Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="name@armellini.com"
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded pl-10 pr-3 py-2 text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Full Display Name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="E.g., John Doe"
                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-3 py-2 text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Assigned Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as UserRole)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-3 py-2 text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 cursor-pointer"
              >
                <option value="sales">Sales Agent (standard CRM access)</option>
                <option value="editor">CRM Editor (access to everything except User Access Control)</option>
                <option value="warehouse">Warehouse Agent (Directory, Analytics, Non-Floral only)</option>
                <option value="admin">Administrator (full access)</option>
              </select>
            </div>

            {statusMsg && (
              <div className={`p-3 rounded text-xs ${statusMsg.success ? 'bg-emerald-50 dark:bg-emerald-950/25 text-emerald-800 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-950/25 text-red-800 dark:text-red-300'}`}>
                {statusMsg.msg}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-xs py-2 rounded-lg transition-colors cursor-pointer"
            >
              {loading ? 'Provisioning...' : 'Provision Account'}
            </button>
          </form>
        </div>

        {/* Users roster list */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-xs font-extrabold text-amber-800 dark:text-amber-300">Enterprise Administration Controls</h4>
              <p className="text-[11px] text-amber-700/90 dark:text-amber-400 leading-relaxed mt-0.5">
                <strong>Administrators</strong> see everything including Settings. <strong>Sales Agent</strong> sees everything except Settings. <strong>Warehouse Agent</strong> is limited to the Consignee &amp; Shipper Directory, Analytics, and Non-Floral LTL.
              </p>
            </div>
          </div>


          <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950">
            <div className="p-4 border-b border-slate-150 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 flex items-center justify-between">
              <h4 className="text-xs font-extrabold text-black dark:text-slate-300 uppercase tracking-widest font-mono">Team Roster</h4>
              <span className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-full">
                {dedupedUsers.length} registered
              </span>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {dedupedUsers.map((user) => (
                <div key={user.uid} className="p-4 flex items-center justify-between hover:bg-slate-50/30 dark:hover:bg-slate-900/10 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${(ROLE_META[user.role] || ROLE_META.sales).iconBg}`}>
                      {user.role === 'admin' ? <ShieldAlert className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-extrabold text-slate-800 dark:text-slate-100">{user.displayName}</span>
                        <span className={`text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.2 rounded ${(ROLE_META[user.role] || ROLE_META.sales).badge}`}>
                          {(ROLE_META[user.role] || ROLE_META.sales).label}
                        </span>
                      </div>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400 block mt-0.5">{user.email}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {onImpersonateUser && user.email.toLowerCase() !== currentUserEmail?.toLowerCase() && (
                      <button
                        onClick={() => onImpersonateUser(user)}
                        className="text-[11px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/60 border border-amber-200 dark:border-amber-800/60 px-2.5 py-1.5 rounded-lg transition flex items-center gap-1 cursor-pointer"
                        title={`Impersonate ${user.displayName} (${user.role.toUpperCase()})`}
                      >
                        <UserCheck className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                        <span className="hidden sm:inline">Impersonate</span>
                      </button>
                    )}

                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value as UserRole)}
                      className="text-[11px] bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 rounded-lg font-bold hover:bg-slate-100 transition cursor-pointer"
                      title="Change role"
                    >
                      <option value="admin">Administrator</option>
                      <option value="editor">CRM Editor</option>
                      <option value="sales">Sales Agent</option>
                      <option value="warehouse">Warehouse Agent</option>
                    </select>

                    {onDeleteUser && user.email !== 'msterling@armellini.com' && (
                      <button
                        onClick={async () => {
                          if (window.confirm(`Revoke CRM access for ${user.displayName}?`)) {
                            await onDeleteUser(user.uid);
                          }
                        }}
                        className="text-[11px] text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 p-2 rounded-lg transition cursor-pointer"
                        title="Remove User"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
